/**
 * The tours: one per landscape (desert, altiplano, volcano, night), cut by the owner (October 2026)
 * from the 11 on the office price list flyer. Typed and documented by the model
 * (@supay-expeditions/model, ToursFileSchema; each field is described there).
 *
 * The source of truth for every piece. `nx build content` checks it against the zod schema and
 * writes dist/tours.json, which the Typst pieces read (Typst cannot read TypeScript); the website
 * and the scripts import this file directly.
 */
import type { ToursFile } from '@supay-expeditions/model';

const tours = {
  languages: ['es', 'en'],
  cancellation: 'Free cancellation up to 24 hours before departure.',
  'prices-valid': '1 October 2026 to 31 March 2027',
  sample: [
    'languages',
    'cancellation',
    'prices-valid',
    'tours.0.difficulty',
    'tours.0.group-size',
    'tours.0.payment',
    'tours.1.difficulty',
    'tours.1.group-size',
    'tours.1.payment',
    'tours.2.difficulty',
    'tours.2.group-size',
    'tours.2.payment',
    'tours.2.confirm (name taken as read)',
    'tours.3.difficulty',
    'tours.3.group-size',
    'tours.3.payment',
    'tours.3.confirm (name taken as read)',
    'tours.0.details.walking (about 1 km)',
    'tours.3.road (observation site)',
    'tours.1.photo (stand-in: the road to the high plateau, not El Tatio)',
    'tours.3.photo (stand-in: Licancabur by day, not the night sky)',
    'every [confirm] mark removed: the facts are taken as checked, not checked by a guide',
  ],
  tours: [
    {
      name: 'Valle de la Luna',
      id: 'valle-de-la-luna',
      photo: 'valle-de-la-luna-tres-marias-8852',
      category: 'desert',
      gloss: 'Moon Valley',
      time: '14:00 – 18:00',
      stops: ['Duna Mayor', 'Tres Marías', 'Anfiteatro'],
      includes: ['Pick-up at your lodging', 'Snack and cocktail at sunset'],
      entry: 10800,
      price: 40000,
      duration: 4,
      altitude: 2540,
      difficulty: 'Moderate',
      'group-size': 12,
      payment: {
        tour: 'Cash, card or transfer',
        'entry-who': 'You, at the park entrance',
        'entry-how': 'Cash or card',
      },
      road: {
        file: 'valle-de-la-luna',
        shape: 'Out and back',
        about:
          'San Pedro west into the Cordillera de la Sal: Duna Mayor, Tres Marías, the Anfiteatro, back on the same road.',
        'return-from': 3,
        stops: [
          {
            name: 'San Pedro de Atacama',
            lat: -22.910535,
            lon: -68.199976,
            osm: 'the start of data/roads/tatio.json, the centre of San Pedro',
          },
          {
            name: 'Duna Mayor',
            lat: -22.9488301,
            lon: -68.2980752,
            osm: 'viewpoint "Duna Mayor I"',
            glyph: 'desert',
          },
          {
            name: 'Tres Marías',
            lat: -22.9194222,
            lon: -68.3190103,
            osm: 'viewpoint "Las tres Marías"',
          },
          {
            name: 'Anfiteatro',
            lat: -22.9398264,
            lon: -68.3000058,
            osm: 'attraction "Anfiteatro"',
          },
          {
            name: 'San Pedro de Atacama',
            lat: -22.910535,
            lon: -68.199976,
            osm: 'the start of data/roads/tatio.json, the centre of San Pedro',
          },
        ],
      },
      details: {
        subtitle: 'Moon Valley, at sunset',
        lead: 'An afternoon in the Cordillera de la Sal, about 13 km west of San Pedro by road. We walk up Duna Mayor, the big dune, and look out over ridges of salt and clay. Then a snack and a cocktail while the sun sets and the light goes red.',
        formed:
          'The Cordillera de la Sal is the bed of an old salt lake. Over millions of years its layers of salt, clay and gypsum were pushed up and folded by the same forces that raise the Andes. Wind and the rare rain cut them into ridges and hollows. The white on the ground is salt, drawn up to the surface by the little moisture there is.',
        stops: [
          'A large dune leaning on a wall of folded rock, with a lookout at the top.',
          'Three figures of rock and salt shaped by the wind.',
          'A curved wall of tilted layers, open like the seats of a theatre.',
        ],
        walking:
          'Short walks on sand and packed earth. The path up Duna Mayor runs one way and climbs steadily for about 1 km. With the stop at the lookout it takes at most 40 minutes.',
        bring: [
          'Sunscreen and sunglasses',
          'Hat',
          'Water bottle',
          'Closed shoes for sand',
          'A warm layer for after sunset',
        ],
        before:
          'There is little shade, and the afternoon sun is strong. After sunset the air cools quickly.',
        respect:
          'We stay on the marked paths. The salt crust breaks underfoot and takes years to form again.',
        tags: ['walking'],
      },
    },
    {
      name: 'Geysers del Tatio',
      id: 'geysers-del-tatio',
      photo: 'altiplano-road-volcanoes-9089',
      category: 'volcano',
      gloss: 'El Tatio geysers',
      time: '4:30 – 10:30',
      stops: ['Geothermal field', 'Vado Putana', 'Machuca'],
      includes: ['Pick-up at your lodging', 'Buffet breakfast'],
      entry: 15000,
      price: 45000,
      duration: 6,
      altitude: 4490,
      difficulty: 'Easy',
      'group-size': 12,
      payment: {
        tour: 'Cash, card or transfer',
        'entry-who': 'You, at the geyser field',
        'entry-how': 'Cash (CLP)',
      },
      road: {
        file: 'tatio',
        shape: 'Out and back',
        about:
          'San Pedro up to the geysers, down past Vado Putana to Machuca, back on the same road.',
        'return-from': 3,
        stops: [
          {
            name: 'San Pedro de Atacama',
            lat: -22.910535,
            lon: -68.199976,
            osm: 'the start of data/roads/tatio.json, the centre of San Pedro',
          },
          {
            name: 'Geysers del Tatio',
            lat: -22.3304893,
            lon: -68.0110854,
            osm: 'parking "Geysers El Tatio", Ruta B-245',
            glyph: 'volcano',
          },
          {
            name: 'Vado Putana',
            lat: -22.52433,
            lon: -68.03661,
            osm: 'no OpenStreetMap feature: the point on the road of data/roads/tatio.json at km 114.5',
          },
          {
            name: 'Machuca',
            lat: -22.5979465,
            lon: -68.0642025,
            osm: 'hamlet "Machuca"',
          },
          {
            name: 'San Pedro de Atacama',
            lat: -22.910535,
            lon: -68.199976,
            osm: 'the start of data/roads/tatio.json, the centre of San Pedro',
          },
        ],
      },
      details: {
        subtitle: 'El Tatio geysers, at sunrise',
        lead: 'We leave San Pedro in the dark and climb to the geothermal field of El Tatio for sunrise. In the cold of the early morning the steam stands highest. Breakfast is at the geysers, and on the way back we stop at Vado Putana and the village of Machuca.',
        formed:
          'Under El Tatio, rain and snowmelt seep down until they meet rock heated by magma. The water boils under pressure and forces its way up through cracks, as steam vents, boiling pools and small geysers. Here the force of the earth that the name Supay stands for is in the open.',
        stops: [
          'Steam vents, boiling pools and small geysers, seen from marked paths.',
          'A high wetland on the Putana river, with birds such as giant coots.',
          'A small Lickanantay village of adobe houses with thatched roofs, where families keep llamas.',
        ],
        walking:
          'Short walks on flat, marked paths in the geothermal field. At this height, slowly.',
        bring: [
          'Warm jacket',
          'Hat and gloves',
          'Warm layers you can take off',
          'Sunscreen and sunglasses',
          'Water bottle',
        ],
        before:
          'El Tatio lies at about 4,300 m, and the road climbs to about 4,500 m. Before sunrise it is usually below 0 °C. Spend a day or two in San Pedro first and drink water. With a heart or lung condition, in pregnancy or with small children, ask a doctor before you book. General advice, not medical advice.',
        respect:
          'Water in the pools can be close to boiling, and the crust around them is thin. Stay on the marked paths.',
        tags: ['cold'],
      },
    },
    {
      name: 'Lagunas Altiplánicas',
      id: 'lagunas-altiplanicas',
      photo: 'lagunas-altiplanicas-road-8876',
      category: 'altiplano',
      gloss: 'high-plateau lagoons and Red Rocks',
      time: '6:00 – 16:00',
      stops: [
        'Laguna Chaxa (Reserva de Flamencos)',
        'Trópico de Capricornio',
        'Lagunas Miscanti y Miñiques',
        'Piedras Rojas',
      ],
      includes: ['Pick-up at your lodging', 'Buffet breakfast', 'Lunch'],
      entry: 'Nac. $23.000 · Ext. $29.000',
      price: 65000,
      duration: 10,
      altitude: 4230,
      difficulty: 'Easy',
      'group-size': 12,
      payment: {
        tour: 'Cash, card or transfer',
        'entry-who': 'You, at each site',
        'entry-how': 'Cash (CLP)',
      },
      road: {
        file: 'lagunas-altiplanicas',
        shape: 'Out and back',
        about:
          'San Pedro south past Laguna Chaxa and the Tropic of Capricorn up to Miscanti and Miñiques and Piedras Rojas, back on Ruta 23.',
        'return-from': 4,
        stops: [
          {
            name: 'San Pedro de Atacama',
            lat: -22.910535,
            lon: -68.199976,
            osm: 'the start of data/roads/tatio.json, the centre of San Pedro',
          },
          {
            name: 'Laguna Chaxa',
            lat: -23.2870043,
            lon: -68.1746309,
            osm: 'information office "Laguna Chaxa"',
          },
          {
            name: 'Trópico de Capricornio',
            lat: -23.4476138,
            lon: -67.9959594,
            osm: 'attraction "Trópico de Capricornio", Ruta 23',
          },
          {
            name: 'Lagunas Miscanti y Miñiques',
            lat: -23.7570581,
            lon: -67.7920229,
            osm: 'viewpoint "Laguna Miñiques"',
            glyph: 'altiplano',
          },
          {
            name: 'Piedras Rojas',
            lat: -23.9213249,
            lon: -67.6964003,
            osm: 'viewpoint "Piedras Rojas", Ruta 23',
          },
          {
            name: 'San Pedro de Atacama',
            lat: -22.910535,
            lon: -68.199976,
            osm: 'the start of data/roads/tatio.json, the centre of San Pedro',
          },
        ],
      },
      details: {
        subtitle: 'High-plateau lagoons and Red Rocks, a full day',
        lead: 'A full day south of San Pedro, from the flamingos of Laguna Chaxa up to the blue lagoons of Miscanti and Miñiques and the red rock of Piedras Rojas.',
        formed:
          'Miscanti and Miñiques were once a single lake. A lava flow from Miñiques volcano ran across it and cut it in two. At Piedras Rojas the rock is volcanic and its iron has rusted red, beside white salt and green water.',
        stops: [
          'A lagoon in the salt flat where flamingos feed.',
          'A sign on the road south where it crosses the Tropic of Capricorn.',
          'Two deep-blue lagoons at about 4,170 m, under the volcanoes of the same names.',
          'Red rock with veins of salt beside the Salar de Aguas Calientes.',
        ],
        walking:
          'Short walks on flat paths at each stop. Above 4,000 m the air is thin; walk slowly.',
        bring: [
          'Warm jacket',
          'Hat',
          'Sunscreen and sunglasses',
          'Water bottle',
          'Cash for the entry fees',
        ],
        before:
          'The lagoons lie at about 4,170 m and Piedras Rojas at about 3,970 m; the road climbs to about 4,230 m. Spend a day or two in San Pedro first, drink water and eat lightly. With a heart or lung condition, in pregnancy or with small children, ask a doctor before you book. This is general advice, not medical advice.',
        named: 3,
        respect:
          'At Laguna Chaxa we keep our voices low and stay behind the markers, so the flamingos go on feeding.',
        tags: [],
      },
    },
    {
      name: 'Tour Astronómico',
      id: 'tour-astronomico',
      photo: 'licancabur-volcano-oasis-8771',
      category: 'night',
      'name-en': 'Astronomical tour',
      time: '20:00 – 23:00',
      stops: ['Telescope observation', 'Photo session'],
      includes: [
        'Pick-up at your lodging',
        'Snack and hot drinks',
        'Drop-off at your lodging',
      ],
      entry: 0,
      price: 30000,
      duration: 3,
      altitude: 2440,
      difficulty: 'Mostly in the vehicle',
      'group-size': 10,
      payment: {
        tour: 'Cash, card or transfer',
        'entry-who': 'No entry ticket',
        'entry-how': 'No entry fee',
      },
      road: {
        file: 'tour-astronomico',
        shape: 'Out and back',
        about:
          'San Pedro south to the dark sky beside the Ayllu de Solor, back on the same road.',
        'return-from': 1,
        stops: [
          {
            name: 'San Pedro de Atacama',
            lat: -22.910535,
            lon: -68.199976,
            osm: 'the start of data/roads/tatio.json, the centre of San Pedro',
          },
          {
            name: 'Ayllu de Solor',
            lat: -22.946185,
            lon: -68.185653,
            osm: 'hamlet "Solor" (sample observation site)',
            glyph: 'night',
          },
          {
            name: 'San Pedro de Atacama',
            lat: -22.910535,
            lon: -68.199976,
            osm: 'the start of data/roads/tatio.json, the centre of San Pedro',
          },
        ],
      },
      details: {
        subtitle: 'The night sky over the Atacama',
        lead: 'We drive out of San Pedro, away from its lights, and look at the night sky through telescopes. The guide shows you the constellations, the planets that are up and the Milky Way. Then we take your photograph under the stars.',
        formed:
          'The Atacama is one of the driest places on earth. The air is thin and still, and there are few lights for many kilometres. That is why the large observatories are built here. In Andean astronomy the dark patches of the Milky Way are figures too, among them a llama.',
        stops: [
          'Planets, star clusters and the Moon when it is up, through telescopes.',
          'A photograph of you under the night sky.',
        ],
        walking: 'Almost none. You stand and sit outside.',
        bring: ['Warm jacket', 'Hat and gloves', 'Warm layers'],
        before:
          'Nights are cold. The lowest of the night is about 9 °C in January and just below 0 °C in June and July. What you see depends on a clear sky, and the faint stars show best away from the full moon.',
        respect:
          "We use only red light and point no torch at the sky or the telescopes, so everyone's eyes stay used to the dark.",
        tags: ['cold'],
      },
    },
  ],
} satisfies ToursFile;

export default tours;
